import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type {
  AquixJourneyResult,
  AquixSurfaceResult,
  PlatformHealthAquixRunDetailResponse,
  PlatformHealthAquixRunsResponse,
} from '@/pages/platform-health/types';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ExternalLink, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type Environment = 'staging' | 'production';

function tone(status: string) {
  if (status === 'green' || status === 'PASS') return 'green';
  if (status === 'yellow' || status === 'WARN') return 'yellow';
  return 'red';
}

function Badge({ status }: { status: string }) {
  const colors = {
    green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
    yellow: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
    red: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
  };
  return (
    <span
      className={cn(
        'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        colors[tone(status)]
      )}
    >
      {status}
    </span>
  );
}

function JsonBlock({ value }: { value: unknown }) {
  return (
    <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded bg-zinc-950 p-3 text-[11px] leading-relaxed text-zinc-100">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function EvidenceImage({
  environment,
  runId,
  evidenceKey,
  alt,
}: {
  environment: Environment;
  runId: string;
  evidenceKey: string;
  alt: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Both render sites key this component on environment/runId/evidenceKey, so a
  // different capture arrives as a fresh mount and the useState(null)
  // initializers above perform the reset the effect used to do synchronously.
  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    void api
      .getPlatformHealthAquixEvidence(environment, runId, evidenceKey)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : String(reason));
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [environment, evidenceKey, runId]);
  if (error) {
    return <p className="text-xs text-red-700 dark:text-red-300">Capture unavailable: {error}</p>;
  }
  if (!url) return <p className="text-xs text-gray-500">Loading private capture…</p>;
  return <img src={url} alt={alt} className="max-h-[720px] w-full rounded border object-contain" />;
}

function SurfaceDetail({
  result,
  environment,
  runId,
}: {
  result: AquixSurfaceResult;
  environment: Environment;
  runId: string;
}) {
  const judgment = result.judgment as {
    firstPass?: { model?: string; output?: unknown };
    escalationPass?: { model?: string; output?: unknown };
  } | null;
  // The previous-green capture is addressed by (environment, runId, evidenceKey), so the remount
  // key below must carry all three — deriving runId inline would leave the `?? runId` fallback
  // outside the key, and a runId change with an unchanged screenshotKey would show a stale image.
  const previousGreenRunId = result.previousGreen?.screenshotKey?.split('/')[2] ?? runId;
  return (
    <div
      data-testid="aquix-surface-detail"
      className="border-t bg-gray-50/70 p-4 dark:bg-zinc-900/40"
    >
      <div className="grid gap-4 xl:grid-cols-2">
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide">Current capture</h3>
          {result.screenshotKey ? (
            <EvidenceImage
              key={`${environment}:${runId}:${result.screenshotKey}`}
              environment={environment}
              runId={runId}
              evidenceKey={result.screenshotKey}
              alt={`${result.surfaceId} current capture`}
            />
          ) : (
            <p className="text-xs text-amber-700">No capture was produced.</p>
          )}
        </section>
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide">Previous green</h3>
          {result.previousGreen?.screenshotKey ? (
            <EvidenceImage
              key={`${environment}:${previousGreenRunId}:${result.previousGreen.screenshotKey}`}
              environment={environment}
              runId={previousGreenRunId}
              evidenceKey={result.previousGreen.screenshotKey}
              alt={`${result.surfaceId} previous green capture`}
            />
          ) : (
            <p className="text-xs text-gray-500">No previous-green capture is available.</p>
          )}
        </section>
      </div>
      <dl className="mt-4 grid gap-3 text-xs sm:grid-cols-4">
        <div>
          <dt className="text-gray-500">Reason</dt>
          <dd>{result.reason ?? result.reasonCode ?? 'none'}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Fixture</dt>
          <dd className="break-all">{result.fixtureId ?? 'none'}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Viewport</dt>
          <dd>
            {result.viewport.name} · {result.viewport.width}×{result.viewport.height}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">Judge model</dt>
          <dd>{judgment?.escalationPass?.model ?? judgment?.firstPass?.model ?? 'not judged'}</dd>
        </div>
      </dl>
      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <section>
          <h3 className="mb-2 text-xs font-semibold">Prechecks</h3>
          <JsonBlock value={result.prechecks} />
        </section>
        <section>
          <h3 className="mb-2 text-xs font-semibold">Judge rubric / output</h3>
          <JsonBlock value={result.judgment} />
        </section>
        <section>
          <h3 className="mb-2 text-xs font-semibold">DOM · network · console facts</h3>
          <JsonBlock value={result.facts} />
        </section>
      </div>
    </div>
  );
}

function JourneyPanel({ journey }: { journey: AquixJourneyResult }) {
  return (
    <details
      className="rounded border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800"
      open={tone(journey.verdict ?? 'red') !== 'green'}
    >
      <summary className="flex cursor-pointer items-center gap-2 p-3">
        <span className="font-medium">{journey.journeyId ?? 'journey'}</span>
        <Badge status={journey.verdict ?? 'unknown'} />
        <span className="text-xs text-gray-500">
          {journey.variant?.consent ?? 'consent unknown'} ·{' '}
          {journey.variant?.viewport ?? 'viewport unknown'}
          {journey.variant?.checkoutViewport
            ? ` → ${journey.variant.checkoutViewport}`
            : ''}{' '}
          ·{' '}
          {journey.variant?.region ?? 'region unknown'}
        </span>
      </summary>
      <div className="border-t p-3 text-xs">
        <p>
          <strong>Proven through:</strong> {journey.provenThrough ?? 'not established'}
        </p>
        {(journey.gaps?.length ?? 0) > 0 ? (
          <ul className="mt-2 list-disc pl-5 text-red-700 dark:text-red-300">
            {journey.gaps?.map((gap) => (
              <li key={gap}>{gap}</li>
            ))}
          </ul>
        ) : null}
        <div className="mt-3 grid gap-3 xl:grid-cols-3">
          <section>
            <h4 className="mb-1 font-semibold">Step timeline</h4>
            <JsonBlock value={journey.steps ?? []} />
          </section>
          <section>
            <h4 className="mb-1 font-semibold">Evidence table</h4>
            <JsonBlock value={journey.evidence ?? []} />
          </section>
          <section>
            <h4 className="mb-1 font-semibold">Cleanup receipt</h4>
            <JsonBlock value={journey.cleanup ?? null} />
          </section>
        </div>
      </div>
    </details>
  );
}

export function AquixWorkbenchContent({
  runs,
  detail,
  environment,
  selectedRunId,
  selectedSurfaceId,
  propertyFilter,
  statusFilter,
  manifestView,
  onSelectRun,
  onSelectSurface,
  onSetFilter,
}: {
  runs: PlatformHealthAquixRunsResponse;
  detail: PlatformHealthAquixRunDetailResponse | null;
  environment: Environment;
  selectedRunId: string | null;
  selectedSurfaceId: string | null;
  propertyFilter: string;
  statusFilter: string;
  manifestView: boolean;
  onSelectRun?: (runId: string) => void;
  onSelectSurface?: (surfaceId: string | null) => void;
  onSetFilter?: (key: 'property' | 'status', value: string) => void;
}) {
  const filtered = useMemo(
    () =>
      (detail?.manifest.results ?? []).filter(
        (result) =>
          (propertyFilter === 'all' || result.property === propertyFilter) &&
          (statusFilter === 'all' || tone(result.status) === statusFilter)
      ),
    [detail, propertyFilter, statusFilter]
  );
  if (!runs.available) {
    return (
      <div
        data-testid="aquix-unavailable"
        className="rounded border border-red-300 bg-red-50 p-4 text-sm text-red-900"
      >
        AQUIX history is unavailable. This is not an empty or successful run history.
      </div>
    );
  }
  return (
    <div className="grid gap-5 xl:grid-cols-[280px_minmax(0,1fr)]">
      <aside className="rounded-lg border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800">
        <h2 className="border-b px-3 py-2 text-xs font-semibold uppercase tracking-wide">Runs</h2>
        {runs.runs.length === 0 ? (
          <p className="p-3 text-sm text-gray-500">No AQUIX runs observed.</p>
        ) : (
          <ul className="max-h-[70vh] overflow-auto">
            {runs.runs.map((run) => (
              <li key={run.runId} className="border-b last:border-0">
                <button
                  type="button"
                  onClick={() => onSelectRun?.(run.runId)}
                  className={cn(
                    'w-full p-3 text-left',
                    selectedRunId === run.runId && 'bg-blue-50 dark:bg-blue-950/30'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <Badge status={run.verdict} />
                    <span className="text-[10px] text-gray-500">
                      {new Date(run.completedAt).toLocaleString()}
                    </span>
                  </div>
                  <code className="mt-1 block break-all text-[10px]">{run.runId}</code>
                  <p className="mt-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-300">
                    {run.summary}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>
      <main className="min-w-0">
        {!detail ? (
          <p className="rounded border p-4 text-sm text-gray-500">
            Select a run to inspect its evidence.
          </p>
        ) : manifestView ? (
          <JsonBlock value={detail.manifest} />
        ) : (
          <>
            <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {detail.properties.map((property) => (
                <button
                  key={property.property}
                  type="button"
                  onClick={() => onSetFilter?.('property', property.property)}
                  className="rounded border bg-white p-3 text-left dark:border-zinc-700 dark:bg-zinc-800"
                >
                  <p className="text-xs font-medium">{property.property}</p>
                  <p className="mt-1 text-lg font-semibold">
                    {property.green}/{property.total} green
                  </p>
                  <p className="text-xs text-red-600">
                    {property.red} red · {property.yellow} yellow
                  </p>
                </button>
              ))}
            </section>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <select
                aria-label="Property"
                value={propertyFilter}
                onChange={(event) => onSetFilter?.('property', event.target.value)}
                className="rounded border bg-white px-2 py-1 text-xs dark:bg-zinc-800"
              >
                <option value="all">All properties</option>
                {detail.properties.map((property) => (
                  <option key={property.property} value={property.property}>
                    {property.property}
                  </option>
                ))}
              </select>
              <select
                aria-label="Status"
                value={statusFilter}
                onChange={(event) => onSetFilter?.('status', event.target.value)}
                className="rounded border bg-white px-2 py-1 text-xs dark:bg-zinc-800"
              >
                <option value="all">All statuses</option>
                <option value="red">Red</option>
                <option value="yellow">Yellow</option>
                <option value="green">Green</option>
              </select>
              <span className="text-xs text-gray-500">
                {filtered.length} surface variants · previous green{' '}
                {detail.previousGreenRunId ?? 'not available'}
              </span>
            </div>
            <section
              data-testid="aquix-surface-grid"
              className="mt-3 rounded border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800"
            >
              {filtered.map((result) => {
                const identity = `${result.surfaceId}|${result.fixtureId ?? 'none'}|${result.viewport.name}`;
                const selected = selectedSurfaceId === identity;
                return (
                  <details
                    key={identity}
                    open={selected || tone(result.status) !== 'green'}
                    onToggle={(event) => {
                      const open = event.currentTarget.open;
                      if (open && !selected) onSelectSurface?.(identity);
                      else if (!open && selected) onSelectSurface?.(null);
                    }}
                    className="border-b last:border-0"
                  >
                    <summary className="flex cursor-pointer list-none items-center gap-3 px-3 py-2 text-xs">
                      {tone(result.status) === 'green' ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                      )}
                      <span className="min-w-0 flex-1 truncate font-mono">{result.surfaceId}</span>
                      <span className="hidden text-gray-500 sm:inline">
                        {result.fixtureId ?? 'no fixture'} · {result.viewport.name}
                      </span>
                      <Badge status={result.status} />
                    </summary>
                    {selected || tone(result.status) !== 'green' ? (
                      <SurfaceDetail
                        result={result}
                        environment={environment}
                        runId={detail.manifest.runId}
                      />
                    ) : null}
                  </details>
                );
              })}
            </section>
            <section className="mt-5 space-y-3">
              <h2 className="text-lg font-semibold">Journeys</h2>
              {detail.manifest.journeys.length ? (
                detail.manifest.journeys.map((journey, index) => (
                  <JourneyPanel
                    key={`${journey.journeyId ?? 'journey'}-${index}`}
                    journey={journey}
                  />
                ))
              ) : (
                <p className="rounded border p-3 text-sm text-amber-700">
                  No journey evidence was attached to this run.
                </p>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

export function AquixWorkbenchPage() {
  const [params, setParams] = useSearchParams();
  const environment: Environment =
    params.get('environment') === 'production' ? 'production' : 'staging';
  const selectedRunId = params.get('run');
  const runsQuery = useQuery({
    queryKey: ['platform-health', 'aquix', 'runs', environment],
    queryFn: () => api.getPlatformHealthAquixRuns(environment),
    refetchInterval: 60_000,
  });
  const runs = runsQuery.data?.data;
  const effectiveRunId = selectedRunId ?? runs?.runs[0]?.runId ?? null;
  useEffect(() => {
    if (!selectedRunId && effectiveRunId)
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('run', effectiveRunId);
          return next;
        },
        { replace: true }
      );
  }, [effectiveRunId, selectedRunId, setParams]);
  const detailQuery = useQuery({
    queryKey: ['platform-health', 'aquix', environment, effectiveRunId],
    queryFn: () => api.getPlatformHealthAquixRun(environment, effectiveRunId!),
    enabled: !!effectiveRunId,
  });
  const update = (key: string, value: string | null) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (!value || value === 'all') next.delete(key);
      else next.set(key, value);
      return next;
    });
  return (
    <div>
      <PageHeader
        title="AQUIX UI QA"
        subtitle="Manual-quality automated review across every product surface and journey."
      >
        <div className="flex gap-2">
          <select
            aria-label="Environment"
            value={environment}
            onChange={(event) => {
              update('environment', event.target.value);
              update('run', null);
            }}
            className="rounded border bg-white px-2 py-1 text-sm dark:bg-zinc-800"
          >
            <option value="staging">Staging</option>
            <option value="production">Production</option>
          </select>
          <button
            type="button"
            onClick={() => void Promise.all([runsQuery.refetch(), detailQuery.refetch()])}
            className="inline-flex items-center gap-1 rounded border px-2 py-1 text-sm"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          {runs?.runs.find((run) => run.runId === effectiveRunId)?.runUrl ? (
            <a
              href={runs.runs.find((run) => run.runId === effectiveRunId)?.runUrl ?? '#'}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded border px-2 py-1 text-sm"
            >
              GitHub <ExternalLink className="h-3 w-3" />
            </a>
          ) : null}
        </div>
      </PageHeader>
      {runsQuery.isLoading || detailQuery.isLoading ? (
        <p className="p-4 text-sm text-gray-500">Loading AQUIX evidence…</p>
      ) : runsQuery.error || detailQuery.error ? (
        <p className="rounded border border-red-300 bg-red-50 p-4 text-sm text-red-900">
          {String(runsQuery.error ?? detailQuery.error)}
        </p>
      ) : runs ? (
        <AquixWorkbenchContent
          runs={runs}
          detail={detailQuery.data?.data ?? null}
          environment={environment}
          selectedRunId={effectiveRunId}
          selectedSurfaceId={params.get('surface')}
          propertyFilter={params.get('property') ?? 'all'}
          statusFilter={params.get('status') ?? 'all'}
          manifestView={params.get('view') === 'manifest'}
          onSelectRun={(runId) => {
            update('run', runId);
            update('surface', null);
          }}
          onSelectSurface={(surface) => update('surface', surface)}
          onSetFilter={update}
        />
      ) : null}
    </div>
  );
}
